import { type RegisterParams, registerSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { KeyboardEvent } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { PasswordRequirements } from '@/components/password-requirements'
import {
  SocialSignIn,
  socialProvidersQueryOptions,
  socialSignInSearch,
  useSocialSignInError,
} from '@/components/social-sign-in'
import { Button } from '@/components/ui/button'
import { ClearableInput } from '@/components/ui/clearable-input'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { PageCard } from '@/components/ui/page-card'
import { PasswordInput } from '@/components/ui/password-input'
import { Text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow } from '@/lib/auth-flow'
import { toast } from '@/lib/toast'
import { commonSearchSchema, generateTitle } from '@/lib/utils'

export const Route = createFileRoute('/_signedOut/register')({
  validateSearch: commonSearchSchema.extend(socialSignInSearch),
  loader: ({ context }) => context.queryClient.ensureQueryData(socialProvidersQueryOptions()),
  component: Register,
  head: () => ({ meta: [{ title: generateTitle('Create Account') }] }),
})

function Register() {
  const search = Route.useSearch()
  const isUpgrade = authFlow(search).kind === 'guestUpgrade'
  const navigate = useNavigate()
  // Resetting a password starts from the sign-in form, which asks for the email.
  useSocialSignInError(search.error, () =>
    navigate({ to: '/login', search: (prev) => ({ ...prev, error: undefined }) }),
  )

  return (
    <PageCard
      title={isUpgrade ? 'Save Your Guest Progress' : 'Create Account'}
      description={
        isUpgrade ? (
          <>
            Create an account to save your progress in{' '}
            {search.gameId === 'argus/rampage' ? 'Rampage' : 'this game'}.
          </>
        ) : undefined
      }
      footer={
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <Text size={12}>Already have an account?</Text>

          <Link to="/login" className="flex">
            <Text size={12} font="nbArchitekt" color="gold">
              Sign In
            </Text>
          </Link>
        </div>
      }
    >
      <RegisterForm />
    </PageCard>
  )
}

function RegisterForm() {
  const search = Route.useSearch()
  // Validate on submit, not blur: an error appearing on blur shifts the layout under a
  // click in progress, so a tap on a link or button below the field can miss.
  const form = useForm<RegisterParams>({
    resolver: zodResolver(registerSchema),
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
  })
  const navigate = useNavigate()
  const isBusy = form.formState.isSubmitting

  // Enter moves to the next field, so a half-filled form isn't submitted into errors.
  const focusOnEnter = (next: keyof RegisterParams) => (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      form.setFocus(next)
    }
  }

  const password = useWatch({ control: form.control, name: 'password' })

  const onSubmit = async ({ firstName, lastName, email, password }: RegisterParams) => {
    // Better Auth stores one display name.
    const name = `${firstName} ${lastName}`
    try {
      // Guest upgrades complete at email verification, so sign-up sends no flow headers.
      const { data, error } = await authClient.signUp.email({ name, email, password })

      if (data) {
        return navigate({ to: '/verify', search: { email, type: VerifyType.ConfirmEmail } })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <SocialSignIn flow={authFlow(search)} disabled={isBusy} />

        <div className="grid gap-7 sm:grid-cols-2 sm:gap-4">
          <Controller
            control={form.control}
            name="firstName"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
                <FieldLabel htmlFor="register-first-name">First Name*</FieldLabel>
                <ClearableInput
                  {...field}
                  id="register-first-name"
                  type="text"
                  onClear={() => field.onChange('')}
                  required
                  autoFocus
                  autoComplete="given-name"
                  enterKeyHint="next"
                  onKeyDown={focusOnEnter('lastName')}
                  placeholder="Mojo"
                  readOnly={isBusy}
                  aria-invalid={fieldState.invalid}
                  aria-describedby={fieldState.invalid ? 'register-first-name-error' : undefined}
                />
                <FieldError id="register-first-name-error" errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="lastName"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
                <FieldLabel htmlFor="register-last-name">Last Name*</FieldLabel>
                <ClearableInput
                  {...field}
                  id="register-last-name"
                  type="text"
                  onClear={() => field.onChange('')}
                  required
                  autoComplete="family-name"
                  enterKeyHint="next"
                  onKeyDown={focusOnEnter('email')}
                  placeholder="Doe"
                  readOnly={isBusy}
                  aria-invalid={fieldState.invalid}
                  aria-describedby={fieldState.invalid ? 'register-last-name-error' : undefined}
                />
                <FieldError id="register-last-name-error" errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>

        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <FieldLabel htmlFor="register-email">Email Address*</FieldLabel>
              <ClearableInput
                {...field}
                id="register-email"
                type="email"
                onClear={() => field.onChange('')}
                required
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="next"
                onKeyDown={focusOnEnter('password')}
                placeholder="you@example.com"
                readOnly={isBusy}
                aria-invalid={fieldState.invalid}
                aria-describedby={fieldState.invalid ? 'register-email-error' : undefined}
              />
              <FieldError id="register-email-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid} data-disabled={field.disabled}>
              <FieldLabel htmlFor="register-password">Password*</FieldLabel>
              <PasswordInput
                {...field}
                id="register-password"
                required
                autoComplete="new-password"
                enterKeyHint="go"
                readOnly={isBusy}
                aria-invalid={fieldState.invalid}
                aria-describedby={
                  fieldState.invalid
                    ? 'register-password-requirements register-password-error'
                    : 'register-password-requirements'
                }
              />
              <PasswordRequirements id="register-password-requirements" password={password} />
              <FieldError id="register-password-error" errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Button type="submit" disabled={isBusy} className="w-full" aria-busy={isBusy}>
          {isBusy ? 'Creating account…' : 'Create Account'}
        </Button>
      </FieldGroup>
    </form>
  )
}
